import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { take, tap } from 'rxjs';

import { DateFormatPipe } from '@core/pipes/date-format.pipe';
import { IColumnSort } from '@core/interfaces/itable';
import { ReportService } from '../services/report.service';
import { ReportHeaders } from '../enums/report-headers';
import { IReportFilters, IReportRequest } from '../interfaces/ireport-request';
import { IReportResponse, ISearchReportResponse } from '../interfaces/ireport-response';

/**
 * Results grid for the Reports page.
 * Receives the shared filter state from ReportCardsComponent and owns only
 * what is specific to the grid: paging, sorting, the loaded rows and download.
 * Any change to `filters` resets to page 1 and re-queries.
 */
@Component({
  selector: 'prism-report-results',
  templateUrl: './report-results.component.html',
  styleUrl: './report-results.component.scss',
})
export class ReportResultsComponent implements OnChanges {
  @Input({ required: true }) filters!: IReportFilters;

  private reportService = inject(ReportService);
  private dateFormatter = inject(DateFormatPipe);

  colHeaders = ReportHeaders;
  columns: string[] = ['fullName', 'courseName', 'programYear', 'status', 'districtName', 'schoolName', 'completedOn'];

  pageIndex = 1;
  pageSize = 10;
  totalData = 0;
  data: IReportResponse[] = [];
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumn: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      this.pageIndex = 1;
      this.searchReport();
    }
  }

  buildReportPayload(): IReportRequest {
    return {
      search: this.filters.search,
      organization: this.filters.organization ?? undefined,
      courseId: this.filters.courseId ?? undefined,
      schoolYearId: this.filters.schoolYearId as number,
      completionStatus: this.filters.completionStatus || null,
      pageIndex: this.pageIndex,
      ItemsPerPage: this.pageSize,
      sortDirection: this.sortDirection,
      sortColumn: this.sortColumn,
    };
  }

  searchReport(): void {
    this.reportService.searchReport(this.buildReportPayload()).pipe(
      take(1),
      tap((res: ISearchReportResponse) => {
        this.data = res.items.map(item => ({
          ...item,
          completedOn: item.completedOn ? this.dateFormatter.transform(item.completedOn) : '',
        }));
        this.pageIndex = res.pageIndex;
        this.totalData = res.totalData;
      })
    ).subscribe({
      error: () => {
        // Keep the grid usable if the search fails or times out instead of spinning forever.
        this.data = [];
        this.totalData = 0;
      }
    });
  }

  downloadReport(): void {
    this.reportService.downloadReport(this.buildReportPayload()).pipe(
      take(1)
    ).subscribe((res: any) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.body);
      a.setAttribute('download', 'prism_course_report.xlsx');
      a.click();
    });
  }

  handlePageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.searchReport();
  }

  handlePageSizeChange(pageSize: number): void {
    if (pageSize == null || pageSize === this.pageSize) return;
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.searchReport();
  }

  handleColumnDirectionChange(columnSort: IColumnSort): void {
    this.sortDirection = columnSort.direction as 'asc' | 'desc';
    this.sortColumn = columnSort.column;
    this.searchReport();
  }
}
